import { DollarSign, CreditCard, Gem, TrendingUp, Users } from "lucide-react";

const t = {
  fr: {
    title: "Modèle d'Affaires",
    subtitle: "Multiples Flux de Revenus. Croissance Prévisible.",
    intro: "EcoLunch se monétise par :",
    cards: [
      { icon: CreditCard, title: "Frais d'Abonnement SaaS", desc: "Plans mensuels ou annuels pour les traiteurs", items: ["Nombre d'écoles", "Volume de commandes", "Fonctionnalités avancées"] },
      { icon: DollarSign, title: "Frais de Transaction", desc: "Frais par commande alignés sur l'utilisation", items: ["Faibles et prévisibles", "Évolutifs avec le volume", "Faciles à comprendre"] },
      { icon: Gem, title: "Modules Premium", desc: "Fonctionnalités avancées optionnelles", items: ["Rapports et analyses", "Gestion fournisseurs et commandes", "API double-sync directe avec vos systèmes actuels", "Outils comptables"] },
    ],
    adoptionTitle: "Modèle d'Adoption des Traiteurs",
    adoptionSubtitle: "Évolutif par Conception",
    adoptionQuote: "Les traiteurs disent publiquement : \u00ab Nous utilisons EcoLunch pour nos commandes en ligne. \u00bb",
    benefitsTitle: "Bénéfices :",
    benefits: ["Confiance instantanée avec les parents", "Intégration simplifiée", "Zéro développement personnalisé", "Expansion plus rapide par traiteur", "Réplication plus facile dans de nouvelles régions"],
    impactTitle: "Impact :",
    impactDesc: "Chaque nouveau traiteur apporte : Plusieurs écoles. Des centaines à des milliers de parents. Engagement récurrent quotidien",
    impactBadge: "Cela crée une distribution virale au niveau institutionnel.",
    networkTitle: "Effets de Réseau Parents",
    networkCards: [
      { title: "Avantages pour les Écoles", items: ["Traiteurs pré-qualifiés uniquement", "Zéro risque", "Qualité garantie", "Tranquillité d'esprit"] },
      { title: "Avantages pour les Parents", items: ["Sécurité assurée", "Pratiques durables", "Qualité constante", "Confiance à chaque repas"] },
      { title: "Avantages pour les Traiteurs", items: ["Badge de confiance", "Visibilité accrue", "Accès à plus d'écoles", "Endossement EcoLunch"] },
    ],
  },
  en: {
    title: "Business Model",
    subtitle: "Multiple Revenue Streams. Predictable Growth.",
    intro: "EcoLunch monetizes through:",
    cards: [
      { icon: CreditCard, title: "SaaS Subscription Fees", desc: "Monthly or annual plans for caterers", items: ["Number of schools", "Order volume", "Advanced features"] },
      { icon: DollarSign, title: "Transaction Fees", desc: "Per-order fees aligned with usage", items: ["Low and predictable", "Scalable with volume", "Easy to understand"] },
      { icon: Gem, title: "Premium Modules", desc: "Optional advanced features", items: ["Reports and analytics", "Supplier management", "Direct API sync with your systems", "Accounting tools"] },
    ],
    adoptionTitle: "Caterer Adoption Model",
    adoptionSubtitle: "Scalable by Design",
    adoptionQuote: "\"Caterers publicly say: 'We use EcoLunch for our online orders.'\"",
    benefitsTitle: "Benefits:",
    benefits: ["Instant trust with parents", "Simplified integration", "Zero custom development", "Faster expansion per caterer", "Easier replication in new regions"],
    impactTitle: "Impact:",
    impactDesc: "Each new caterer brings: Multiple schools. Hundreds to thousands of parents. Daily recurring engagement",
    impactBadge: "This creates viral distribution at the institutional level.",
    networkTitle: "Parent Network Effects",
    networkCards: [
      { title: "Benefits for Schools", items: ["Pre-qualified caterers only", "Zero risk", "Guaranteed quality", "Peace of mind"] },
      { title: "Benefits for Parents", items: ["Assured safety", "Sustainable practices", "Consistent quality", "Trust with every meal"] },
      { title: "Benefits for Caterers", items: ["Trust badge", "Increased visibility", "Access to more schools", "EcoLunch endorsement"] },
    ],
  },
};

export default function BusinessSection({ lang }) {
  const c = t[lang];
  return (
    <>
      <section id="business" data-testid="business-section" className="py-16 sm:py-24 bg-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/20 mb-6 glow-primary">
              <DollarSign className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">{c.title}</h2>
            <p className="text-lg sm:text-xl text-gradient-lime font-bold">{c.subtitle}</p>
          </div>
          <div className="max-w-4xl mx-auto mb-8 text-center">
            <p className="text-base sm:text-lg text-white/80">{c.intro}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-16">
            {c.cards.map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.title} className="rounded-xl border text-card-foreground shadow hover-lift bg-dark border-primary/30 hover:border-primary transition-all duration-300">
                  <div className="p-6 pt-6 sm:pt-8 pb-6 sm:pb-8">
                    <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary/20 mb-4 sm:mb-6 glow-primary">
                      <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-primary" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">{card.title}</h3>
                    <p className="text-sm text-white/60 mb-4">{card.desc}</p>
                    <ul className="space-y-2 sm:space-y-3">
                      {card.items.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-2 glow-primary" />
                          <span className="text-sm text-white/70">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/20 mb-6 glow-primary">
                <TrendingUp className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">{c.adoptionTitle}</h2>
              <p className="text-lg sm:text-xl text-white/70">{c.adoptionSubtitle}</p>
            </div>
            <div className="rounded-xl border text-card-foreground shadow bg-dark border-primary/50 glow-strong mb-8">
              <div className="p-6 py-4 sm:py-6">
                <p className="text-base sm:text-lg font-bold text-primary text-center italic px-4">{c.adoptionQuote}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-12">
              <div className="rounded-xl border text-card-foreground shadow bg-dark border-primary/20">
                <div className="p-6">
                  <h4 className="text-lg font-bold text-primary mb-4">{c.benefitsTitle}</h4>
                  <ul className="space-y-2">
                    {c.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-2 glow-primary" />
                        <span className="text-sm text-white/70">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="rounded-xl border text-card-foreground shadow bg-dark border-primary/20">
                <div className="p-6">
                  <h4 className="text-lg font-bold text-white mb-4">{c.impactTitle}</h4>
                  <p className="text-sm text-white/70 mb-4">{c.impactDesc}</p>
                  <div className="inline-flex items-center rounded-md border bg-primary/20 text-primary border-primary/40 text-xs sm:text-sm px-3 py-1 font-semibold">{c.impactBadge}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/20 mb-6 glow-primary">
                <Users className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">{c.networkTitle}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {c.networkCards.map((card) => (
                <div key={card.title} className="rounded-xl border text-card-foreground shadow hover-lift bg-dark border-primary/30 hover:border-primary transition-all duration-300">
                  <div className="p-6 pt-6 sm:pt-8 pb-6 sm:pb-8">
                    <h3 className="text-xl font-bold text-gradient-lime mb-4">{card.title}</h3>
                    <ul className="space-y-2 sm:space-y-3">
                      {card.items.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-2 glow-primary" />
                          <span className="text-sm text-white/70">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
